import { lazy, type ComponentType } from 'react'

const lazyNamed = <T extends ComponentType<unknown>>(
	factory: () => Promise<Record<string, T>>,
	exportName: string
) =>
	lazy(() =>
		factory().then(module => ({
			default: module[exportName] as T
		}))
	)

export const LazyHomePage = lazyNamed(() => import('@/app/pages/Home.page.tsx'), 'HomePage')
export const LazyRequestsPage = lazyNamed(() => import('@/app/pages/requests/Requests.page.tsx'), 'RequestsPage')
export const LazyRequestDetailsPage = lazy(() => import('@/app/pages/requests/details/RequestDetails.page.tsx'))
export const LazyChipProgrammingPage = lazy(() => import('@/app/pages/requests/chip-programming/ChipProgramming.page.tsx'))
export const LazyChipProgrammingUserDataPage = lazy(
	() => import('@/app/pages/requests/chip-programming/user-data/ChipProgrammingUserData.page.tsx')
)
export const LazyChipProgrammingAccessInfoPage = lazy(
	() => import('@/app/pages/requests/chip-programming/access-info/ChipProgrammingAccessInfo.page.tsx')
)
export const LazySettingsPage = lazyNamed(() => import('@/app/pages/settings/Settings.page.tsx'), 'SettingsPage')
export const LazyGuidePage = lazyNamed(() => import('@/app/pages/guide/Guide.page.tsx'), 'GuidePage')
export const LazyReportsPage = lazy(() => import('@/app/pages/reports/Reports.page.tsx'))
export const LazyReportEventsPage = lazyNamed(
	() => import('@/app/pages/reportEvents/ReportEvents.page.tsx'),
	'ReportEventsPage'
)
export const LazyGreetingsPage = lazyNamed(() => import('@/app/pages/greetings/Greetings.page.tsx'), 'GreetingsPage')
export const LazyHandsetDisconnectPage = lazy(() =>
	import('@/app/pages/handsets').then(module => ({ default: module.HandsetDisconnectPage }))
)
export const LazyAddressesPage = lazy(() => import('@/app/pages/addresses'))
export const LazyClientsPage = lazyNamed(() => import('@/app/pages/clients/Clients.page.tsx'), 'ClientsPage')
export const LazyClientEditPage = lazy(() => import('@/app/pages/clients/ClientEdit.page.tsx'))
export const LazyCamerasPage = lazyNamed(() => import('@/app/pages/cameras/Cameras.page.tsx'), 'CamerasPage')
export const LazyCameraCreatePage = lazy(() => import('@/app/pages/cameras/CameraCreate.page.tsx'))
export const LazyCameraEditPage = lazy(() => import('@/app/pages/cameras/CameraEdit.page.tsx'))
export const LazyCameraClientEditPage = lazy(() => import('@/app/pages/cameras/CameraClientEdit.page.tsx'))
export const LazyGatesPage = lazy(() => import('@/app/pages/gates/Gates.page.tsx'))
export const LazyGatesEditPage = lazy(() => import('@/app/pages/gates/edit/GatesEdit.page.tsx'))
export const LazyGatesMassConfigPage = lazy(() => import('@/app/pages/gates/massConfig/GatesMassConfig.page.tsx'))
export const LazyUsersPage = lazyNamed(() => import('@/app/pages/users/Users.page.tsx'), 'UsersPage')
export const LazyAuthzAdminPage = lazyNamed(() => import('@/app/pages/authz-admin/AuthzAdmin.page.tsx'), 'AuthzAdminPage')
export const LazyBrainChatPage = lazyNamed(() => import('@/app/pages/brain-chat/BrainChat.page.tsx'), 'BrainChatPage')
export const LazyDiagnosticsPage = lazyNamed(
	() => import('@/app/pages/intercoms/diagnostics/Diagnostics.page.tsx'),
	'DiagnosticsPage'
)
export const LazyOverviewPage = lazyNamed(
	() => import('@/app/pages/intercoms/diagnostics/menu/overview/Overview.page.tsx'),
	'OverviewPage'
)
export const LazyGeneralPage = lazyNamed(
	() => import('@/app/pages/intercoms/diagnostics/menu/general/General.page.tsx'),
	'GeneralPage'
)
export const LazyKeysPage = lazyNamed(() => import('@/app/pages/intercoms/diagnostics/menu/keys/Keys.page.tsx'), 'KeysPage')
export const LazyCodesPage = lazyNamed(() => import('@/app/pages/intercoms/diagnostics/menu/codes/Codes.page.tsx'), 'CodesPage')
export const LazyApartmentsPage = lazyNamed(
	() => import('@/app/pages/intercoms/diagnostics/menu/apartments/Apartments.page.tsx'),
	'ApartmentsPage'
)
export const LazyMatrixPage = lazyNamed(
	() => import('@/app/pages/intercoms/diagnostics/menu/matrix/Matrix.page.tsx'),
	'MatrixPage'
)
export const LazyDoorsPage = lazyNamed(() => import('@/app/pages/intercoms/diagnostics/menu/doors/Doors.page.tsx'), 'DoorsPage')
export const LazyMarqueePage = lazyNamed(
	() => import('@/app/pages/intercoms/diagnostics/menu/marquee/Marquee.page.tsx'),
	'MarqueePage'
)
export const LazyAudioPage = lazyNamed(() => import('@/app/pages/intercoms/diagnostics/menu/audio/Audio.page.tsx'), 'AudioPage')
export const LazyEqualizerPage = lazyNamed(
	() => import('@/app/pages/intercoms/diagnostics/menu/equalizer/Equalizer.page.tsx'),
	'EqualizerPage'
)
export const LazySipPage = lazyNamed(() => import('@/app/pages/intercoms/diagnostics/menu/sip/Sip.page.tsx'), 'SipPage')
export const LazyVideoPage = lazyNamed(() => import('@/app/pages/intercoms/diagnostics/menu/video/Video.page.tsx'), 'VideoPage')
export const LazyDdnsPage = lazyNamed(() => import('@/app/pages/intercoms/diagnostics/menu/ddns/Ddns.page.tsx'), 'DdnsPage')
export const LazyNetworkPage = lazyNamed(
	() => import('@/app/pages/intercoms/diagnostics/menu/network/Network.page.tsx'),
	'NetworkPage'
)
export const LazySyslogPage = lazyNamed(
	() => import('@/app/pages/intercoms/diagnostics/menu/syslog/Syslog.page.tsx'),
	'SyslogPage'
)
export const LazyFirmwarePage = lazyNamed(
	() => import('@/app/pages/intercoms/diagnostics/menu/firmware/Firmware.page.tsx'),
	'FirmwarePage'
)
export const LazyDatetimePage = lazyNamed(
	() => import('@/app/pages/intercoms/diagnostics/menu/datetime/Datetime.page.tsx'),
	'DatetimePage'
)
export const LazyRebootPage = lazyNamed(
	() => import('@/app/pages/intercoms/diagnostics/menu/reboot/Reboot.page.tsx'),
	'RebootPage'
)
export const LazyResetPage = lazyNamed(() => import('@/app/pages/intercoms/diagnostics/menu/reset/Reset.page.tsx'), 'ResetPage')
export const LazyForbiddenPage = lazyNamed(() => import('@/app/pages/forbidden/Forbidden.page.tsx'), 'ForbiddenPage')
export const LazyLoginPage = lazyNamed(() => import('@/app/pages/login/Login.page.tsx'), 'LoginPage')
